<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import EventsAPI from 'dashboard/api/planning/events';
import { serializeFiltersForApi } from 'dashboard/routes/dashboard/planning/events/eventsDateRange';
import { buildOrderedStatusOptions } from 'dashboard/routes/dashboard/planning/events/statusOptions';

import ChatTypeTabs from 'dashboard/components/widgets/ChatTypeTabs.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import EventsFilter from 'dashboard/routes/dashboard/planning/events/EventsFilter.vue';
import EventInboxCard from './EventInboxCard.vue';

const props = defineProps({
  selectedEventId: {
    type: [String, Number],
    default: null,
  },
});

const PER_PAGE = 25;

const { t } = useI18n();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const events = ref([]);
const meta = ref({ currentPage: 1, perPage: PER_PAGE, totalEntries: 0 });
const isFetching = ref(false);
const isLoadingMore = ref(false);
const listRef = ref(null);
const statusOptions = ref([]);
const tabCounts = ref({ me: 0, unassigned: 0, all: 0 });

const savedFilters = computed(
  () => uiSettings.value.event_inbox_filter_by || {}
);

const activeAssigneeTab = ref(
  savedFilters.value.assignee_type || wootConstants.ASSIGNEE_TYPE.ME
);
const sortBy = ref(savedFilters.value.sort_by || 'updated_at_desc');
const appliedFilters = ref(savedFilters.value.filters || []);

const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');
const agentBots = useMapGetter('agentBots/getBots');
const eventLabels = useMapGetter('planningEventLabels/getRecords');

const [showSortMenu, toggleSortMenu] = useToggle();

const assigneeTabItems = computed(() => [
  {
    key: wootConstants.ASSIGNEE_TYPE.ME,
    name: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.me'),
    count: tabCounts.value.me,
  },
  {
    key: wootConstants.ASSIGNEE_TYPE.UNASSIGNED,
    name: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.unassigned'),
    count: tabCounts.value.unassigned,
  },
  {
    key: wootConstants.ASSIGNEE_TYPE.ALL,
    name: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.all'),
    count: tabCounts.value.all,
  },
]);

const sortOptions = computed(() => [
  {
    value: 'updated_at_desc',
    label: t('EVENT_INBOX.SORT.UPDATED_NEWEST'),
  },
  {
    value: 'updated_at_asc',
    label: t('EVENT_INBOX.SORT.UPDATED_OLDEST'),
  },
  {
    value: 'created_at_desc',
    label: t('EVENT_INBOX.SORT.CREATED_NEWEST'),
  },
  {
    value: 'created_at_asc',
    label: t('EVENT_INBOX.SORT.CREATED_OLDEST'),
  },
  {
    value: 'event_occurrence_desc',
    label: t('EVENT_INBOX.SORT.EVENT_LATEST'),
  },
  {
    value: 'event_occurrence_asc',
    label: t('EVENT_INBOX.SORT.EVENT_OLDEST'),
  },
]);

const hasMore = computed(() => {
  return events.value.length < (meta.value.totalEntries || 0);
});

const showEmptyState = computed(
  () => !isFetching.value && events.value.length === 0
);

const infiniteLoaderOptions = computed(() => ({
  root: listRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

const persistFilters = () => {
  updateUISettings({
    event_inbox_filter_by: {
      assignee_type: activeAssigneeTab.value,
      sort_by: sortBy.value,
      filters: appliedFilters.value,
    },
  });
};

const assigneeFilterRule = () => {
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return {
      attributeKey: 'event_manager_id',
      filterOperator: 'equal_to',
      values: currentUser.value?.id,
    };
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return {
      attributeKey: 'event_manager_id',
      filterOperator: 'equal_to',
      values: 'null',
    };
  }
  return null;
};

const buildRequestFilters = () => {
  const filters = [...appliedFilters.value];
  const assigneeRule = assigneeFilterRule();
  if (assigneeRule) {
    // Drop conflicting manager filters from advanced filters so the tab wins
    const withoutManager = filters.filter(
      f => (f.attributeKey || f.attribute_key) !== 'event_manager_id'
    );
    withoutManager.unshift(assigneeRule);
    return serializeFiltersForApi(withoutManager);
  }
  return serializeFiltersForApi(filters);
};

const buildParams = (page = 1) => {
  const filters = buildRequestFilters();
  return {
    page,
    per_page: PER_PAGE,
    sort_by: sortBy.value,
    ...(filters.length ? { filters: JSON.stringify(filters) } : {}),
  };
};

const fetchTabCounts = async () => {
  const userId = currentUser.value?.id;
  const base = {
    per_page: 1,
    page: 1,
    sort_by: sortBy.value,
  };
  const advancedWithoutManager = serializeFiltersForApi(
    appliedFilters.value.filter(
      f => (f.attributeKey || f.attribute_key) !== 'event_manager_id'
    )
  );

  const requests = [
    EventsAPI.getAll({
      ...base,
      filters: JSON.stringify([
        ...advancedWithoutManager,
        ...(userId
          ? [
              {
                attribute_key: 'event_manager_id',
                filter_operator: 'equal_to',
                values: [String(userId)],
                query_operator: 'and',
              },
            ]
          : []),
      ]),
    }),
    EventsAPI.getAll({
      ...base,
      filters: JSON.stringify([
        ...advancedWithoutManager,
        {
          attribute_key: 'event_manager_id',
          filter_operator: 'equal_to',
          values: ['null'],
          query_operator: 'and',
        },
      ]),
    }),
    EventsAPI.getAll({
      ...base,
      ...(advancedWithoutManager.length
        ? { filters: JSON.stringify(advancedWithoutManager) }
        : {}),
    }),
  ];

  try {
    const [mine, unassigned, all] = await Promise.all(requests);
    tabCounts.value = {
      me: mine.data.meta?.total_entries || 0,
      unassigned: unassigned.data.meta?.total_entries || 0,
      all: all.data.meta?.total_entries || 0,
    };
  } catch {
    // counts are optional
  }
};

const fetchEvents = async ({ reset = true } = {}) => {
  if (reset) {
    isFetching.value = true;
  } else {
    isLoadingMore.value = true;
  }

  const page = reset ? 1 : (meta.value.currentPage || 1) + 1;

  try {
    const response = await EventsAPI.getAll(buildParams(page));
    const { data: pageEvents, meta: responseMeta } = response.data;
    const list = Array.isArray(pageEvents) ? pageEvents : [];

    events.value = reset ? list : [...events.value, ...list];
    if (responseMeta) {
      meta.value = {
        currentPage: responseMeta.current_page,
        perPage: responseMeta.per_page,
        totalEntries: responseMeta.total_entries,
      };
    }
  } catch {
    if (reset) events.value = [];
  } finally {
    isFetching.value = false;
    isLoadingMore.value = false;
  }
};

const loadMore = () => {
  if (!hasMore.value || isFetching.value || isLoadingMore.value) return;
  fetchEvents({ reset: false });
};

const refresh = async () => {
  persistFilters();
  await Promise.all([fetchEvents({ reset: true }), fetchTabCounts()]);
};

const onTabChange = key => {
  if (activeAssigneeTab.value === key) return;
  activeAssigneeTab.value = key;
  refresh();
};

const onSortChange = value => {
  sortBy.value = value;
  toggleSortMenu(false);
  refresh();
};

const onFiltersApply = filters => {
  appliedFilters.value = filters;
  refresh();
};

const loadStatusOptions = async () => {
  const response = await store.dispatch('planningEvents/getStatusOptions');
  statusOptions.value = [...buildOrderedStatusOptions(response)];
};

watch(
  () => currentUser.value?.id,
  id => {
    if (id) fetchTabCounts();
  }
);

watch(
  () => props.selectedEventId,
  id => {
    if (!id) return;
    const index = events.value.findIndex(
      item => String(item.id) === String(id)
    );
    if (index < 0 || !events.value[index]?.unread) return;
    // Match Show.vue auto mark-read delay so the list unread state stays in sync
    window.setTimeout(() => {
      const current = events.value[index];
      if (current && String(current.id) === String(id) && current.unread) {
        events.value[index] = { ...current, unread: false };
      }
    }, 3500);
  }
);

onMounted(async () => {
  try {
    await Promise.all([
      loadStatusOptions(),
      store.dispatch('agents/get'),
      store.dispatch('agentBots/get'),
      store.dispatch('planningEventLabels/get'),
      store.dispatch('attributes/get'),
    ]);
  } catch {
    // optional filter metadata; list fetch should still run
  }
  await refresh();
});
</script>

<template>
  <div
    class="flex flex-col h-full bg-white dark:bg-n-solid-1 border-r border-n-weak w-[340px] 2xl:w-[412px]"
  >
    <div class="flex items-center justify-between gap-2 px-4 py-5">
      <h1 class="text-xl font-medium truncate text-n-slate-12 mb-0">
        {{ t('EVENT_INBOX.TITLE') }}
      </h1>
      <div class="flex items-center gap-1 shrink-0">
        <EventsFilter
          v-model="appliedFilters"
          icon-only
          :status-options="statusOptions"
          :agents="agents"
          :agent-bots="agentBots"
          :event-labels="eventLabels"
          :current-user="currentUser"
          @apply="onFiltersApply"
        />
        <div class="relative flex">
          <Button
            v-tooltip.right="t('EVENT_INBOX.SORT.LABEL')"
            xs
            slate
            faded
            icon="i-lucide-arrow-up-down"
            :aria-label="t('EVENT_INBOX.SORT.LABEL')"
            @click="toggleSortMenu()"
          />
          <div
            v-if="showSortMenu"
            v-on-click-outside="() => toggleSortMenu(false)"
            class="mt-1 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-72 rounded-xl p-1 absolute z-40 top-full ltr:right-0 rtl:left-0"
          >
            <button
              v-for="option in sortOptions"
              :key="option.value"
              type="button"
              class="w-full text-left px-3 py-2 text-sm rounded-lg"
              :class="
                sortBy === option.value
                  ? 'bg-woot-50 text-woot-600 dark:bg-woot-900/20 dark:text-woot-400 font-medium'
                  : 'text-n-slate-12 hover:bg-n-alpha-2'
              "
              @click="onSortChange(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <ChatTypeTabs
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      @chat-tab-change="onTabChange"
    />

    <div
      ref="listRef"
      class="flex-1 overflow-y-auto min-h-0 bg-white dark:bg-n-solid-1"
    >
      <div v-if="isFetching" class="flex items-center justify-center py-10">
        <Spinner />
      </div>

      <div
        v-else-if="showEmptyState"
        class="flex flex-col items-center justify-center px-6 py-12 text-center"
      >
        <span class="i-lucide-calendar-x size-10 text-n-slate-6 mb-3" />
        <p class="text-sm text-n-slate-11 mb-0">
          {{ t('EVENT_INBOX.EMPTY_LIST') }}
        </p>
      </div>

      <template v-else>
        <div class="flex flex-col">
          <EventInboxCard
            v-for="event in events"
            :key="event.id"
            :event="event"
            :active="String(selectedEventId) === String(event.id)"
          />
        </div>
        <div v-if="isLoadingMore" class="flex items-center justify-center py-4">
          <Spinner />
        </div>
        <p
          v-else-if="!hasMore"
          class="text-xs text-center text-n-slate-10 py-4 mb-0"
        >
          {{ t('EVENT_INBOX.EOF') }}
        </p>
        <IntersectionObserver
          v-else
          :options="infiniteLoaderOptions"
          @observed="loadMore"
        />
      </template>
    </div>
  </div>
</template>
